import { useState } from 'react';
import {
  BarChart3,
  BookOpen,
  ChevronLeft,
  FileKey,
  LayoutDashboard,
  Leaf,
  Package,
  Settings,
  Shield,
  TrendingUp,
  CalendarDays,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export type AdminPanel =
  | 'overview'
  | 'analytics'
  | 'apps'
  | 'revenue'
  | 'events'
  | 'team'
  | 'keys'
  | 'settings'
  | 'docs'
  | 'app-detail';

interface AdminSidebarProps {
  selectedPanel: AdminPanel;
  onSelectPanel: (panel: AdminPanel) => void;
  onExit: () => void;
}

const navGroups: Array<{
  label: string;
  items: Array<{ label: string; panel: AdminPanel; icon: typeof LayoutDashboard }>;
}> = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', panel: 'overview', icon: LayoutDashboard },
      { label: 'Analytics', panel: 'analytics', icon: BarChart3 },
    ],
  },
  {
    label: 'Apps & Content',
    items: [
      { label: 'Applications', panel: 'apps', icon: Package },
      { label: 'Revenue', panel: 'revenue', icon: TrendingUp },
      { label: 'Events', panel: 'events', icon: CalendarDays },
    ],
  },
  {
    label: 'Access',
    items: [
      { label: 'Users / Permissions', panel: 'team', icon: Shield },
      { label: 'Keys', panel: 'keys', icon: FileKey },
      { label: 'Settings', panel: 'settings', icon: Settings },
      { label: 'Docs', panel: 'docs', icon: BookOpen },
    ],
  },
];

export default function AdminSidebar({ selectedPanel, onSelectPanel, onExit }: AdminSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={cn(
        'sticky top-0 flex h-full min-h-full shrink-0 flex-col border-r border-border bg-card/70 backdrop-blur-xl transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex h-16 items-center gap-3 border-b border-border px-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Leaf className="h-4 w-4" />
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-foreground">Maple Market Admin</div>
            <div className="text-xs text-muted-foreground">Embedded inside MapleOS</div>
          </div>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground"
          onClick={() => setCollapsed((current) => !current)}
        >
          <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} />
        </Button>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto px-2 py-4">
        {navGroups.map((group) => (
          <div key={group.label}>
            {!collapsed && (
              <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground/70">
                {group.label}
              </div>
            )}
            <div className="space-y-1">
              {group.items.map((item) => {
                const active = selectedPanel === item.panel;

                return (

                  <button
                    key={item.panel}
                    type="button"
                    onClick={() => onSelectPanel(item.panel)}
                    title={collapsed ? item.label : undefined}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                      active
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    )}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-border p-3">
        <Button type="button" variant="ghost" className="w-full justify-start gap-2" onClick={onExit}>
          <ChevronLeft className="h-4 w-4" />
          {!collapsed && <span>Back to Store</span>}
        </Button>
      </div>
    </aside>
  );
}
